import { Badge, dateFromObjectId, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, isOtherSellerPurchase } from "common";
import dayjs, { Dayjs } from "dayjs";
import React, { cloneElement, ReactElement, ReactNode } from "react";
import { Message, Permission, Purchase, User } from "types";
import { useTraveler } from "../../../hooks/resources/useTraveler";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useResource } from "../../../hooks/useResource";
import { LocalizationProps } from "../../../models/Locale";
import { isSplitPayPurchase } from "../../../models/Purchase";
import { NoTranslate } from "../../Common/NoTranslate";
import { getSeller } from "@/utils/getSeller";

interface TimelineItemProps {
    title: string;
    showDate?: boolean;
    icon: ReactElement;
    badge?: ReactElement;
    color?: string;
    audit?: { id?: ID; createdAt: Dayjs; createdBy: { id: ID } };
    message?: Message;
    children?: ReactNode;
    traveler?: { id: ID; name?: string };
    purchase?: Purchase;
    senderName?: string;
}

export const TimelineItem = ({
    title,
    icon,
    badge,
    audit,
    message,
    showDate = false,
    color = "text-blue",
    children,
    traveler,
    purchase,
    senderName,
}: TimelineItemProps) => {
    const { isGranted } = useAuthorization();
    const userId = audit?.createdBy?.id ?? message?.from ?? traveler?.id;

    const isAuditCreatorNotTraveler = userId !== traveler?.id && userId !== purchase?.organizer?.id;
    const shouldFetchUser = isAuditCreatorNotTraveler && !senderName;

    const { data: fetchedUser } = useResource<User>(["users", userId], null, { enabled: shouldFetchUser });
    const isOtherSellerPurchaseTimelineItem = purchase && isOtherSellerPurchase(purchase, getSeller().id);
    const { data: fetchedTraveler } = useTraveler(traveler?.id ?? purchase?.organizer?.id, null, {
        enabled:
            shouldFetchUser && isGranted(Permission.MODIFY_PURCHASE_CONTACT_VIEW) && !isOtherSellerPurchaseTimelineItem,
    });

    const user = fetchedUser ?? fetchedTraveler;

    if (audit && !audit?.createdAt) {
        returnError(audit, title);
    }

    if (message && !(message?.updatedAt || message?.createdAt)) {
        returnError(message, title);
    }

    // since the getCreatedDate function can return null, we need to use optional chaining when we call the createdAt methods
    const createdAt = getCreatedDate(audit, message);

    const fromNow = createdAt?.fromNow();
    const todayYear = dayjs().format("YYYY");
    const itemYear = createdAt?.format("YYYY");
    let displayDate = formatDate(createdAt, DateFormat.DATE_TIME_SHORT);
    const timeInUserTimezone = formatDate(createdAt?.tz(dayjs.tz.guess()), DateFormat.DATE_TIME_SHORT);
    const isSameDate = displayDate === timeInUserTimezone;
    if (todayYear === itemYear) {
        displayDate = displayDate.replace(/\w?20\d{2}/, "").trim();
    }

    const tooltipMessage = (
        <span className="text-sm">
            <NoTranslate>
                {createdAt?.format("llll")} ~{fromNow}
            </NoTranslate>

            {isSameDate ? null : (
                <div className="pt-1 text-gray">
                    In your timezone: <NoTranslate>{timeInUserTimezone}</NoTranslate>
                </div>
            )}
        </span>
    );
    const isSplitPayment = purchase && isSplitPayPurchase(purchase);

    const fallbackName = senderName ?? user?.name ?? "N/A";
    const payerName = isSplitPayment ? traveler?.name ?? fallbackName : fallbackName;

    return (
        <div data-audit-id={audit?.id} className="flex w-full flex-row xl:pl-6" data-testid="timeline-item">
            <div className="flex flex-row">
                <div className="flex flex-col border-r border-gray-light pr-4">
                    <div
                        className={clsx(
                            "flex w-8 flex-col items-center justify-start space-y-1.5 font-bold",
                            !showDate && "invisible",
                        )}
                    >
                        <NoTranslate className="text-sm text-gray-darker">{createdAt?.format("MMM")}</NoTranslate>
                        <NoTranslate as="div" className="text-2xl leading-6">
                            {createdAt?.format("DD")}
                        </NoTranslate>
                        <NoTranslate className="text-sm">{createdAt?.format("ddd")}</NoTranslate>
                    </div>
                </div>
                <div className="ml-4 flex items-start pt-1">
                    <div
                        className={clsx(
                            "flex h-8 w-8 items-center justify-center rounded-full  pt-0.5",
                            // Using string concat to add colors. Won't be picked up by TW JIT but will work because we use them
                            // elsewhere in the project
                            `border border-${color}`,
                        )}
                    >
                        {cloneElement(icon, { className: `text-${color}` })}
                    </div>
                </div>
            </div>
            <div className="w-full flex-grow pb-10 pl-4">
                <div className="flex w-full justify-between text-xs text-gray-darker">
                    <Tooltip allowHTML content={tooltipMessage}>
                        <NoTranslate as="div" data-testid="timeline-item-display-date">
                            {displayDate}
                        </NoTranslate>
                    </Tooltip>
                    <div
                        className="flex justify-start truncate text-xs text-gray-dark"
                        data-testid="timeline-item-user-name"
                    >
                        <NoTranslate as="div">- {payerName}</NoTranslate>
                        {/* The user?.name in this context is organizer one by default */}
                    </div>
                </div>

                <div className="w-full space-y-3">
                    <div className="flex items-center justify-between">
                        <h4 className="font-bold" data-testid="timeline-item-title">
                            {title}
                        </h4>
                        {badge}
                    </div>

                    <div className="items-center space-x-2" data-testid="timeline-item-content">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

const returnError = (item: Message | { id?: ID; createdAt: Dayjs; createdBy: { id: ID } }, title: string) => {
    console.log("ERROR: Cannot show timeline tile due to missing createdAt | updatedAt", title, item);
    return null;
};

const getCreatedDate = (audit?: { id?: ID; createdAt: Dayjs; createdBy: { id: string } }, message?: Message) => {
    if (audit) {
        return audit.createdAt;
    }

    if (message?.createdAt) {
        return message.createdAt;
    }

    if (message?.id) {
        return dateFromObjectId(message.id);
    }

    return null;
};

export const TimelinePill = ({
    icon,
    children,
    className,
}: {
    icon?: ReactNode;
    children: ReactNode;
    className?: string;
}) => {
    return (
        <Badge
            size="medium"
            color="secondary"
            className={clsx("h-auto !items-start space-x-1 !rounded !px-2 xl:px-3", className)}
        >
            {icon && <span className="align-top">{icon}</span>}
            <span className="text-sm xl:text-base">{children}</span>
        </Badge>
    );
};

interface TimelineItemReasonProps extends LocalizationProps {
    children: ReactNode;
    className?: string;
}

export const TimelineItemReason = ({ children, className, localize = false }: TimelineItemReasonProps) => {
    return (
        <div
            className={clsx(
                "inline-block max-w-full self-start rounded bg-gray-lighter p-3",
                { "no-translate": !localize },
                className,
            )}
            data-testid="message-timeline-item-reason"
        >
            {children}
        </div>
    );
};
